import { nextTick, ref, type Ref } from 'vue'

export type RichTextFormat = 'bold' | 'italic' | 'strikethrough' | 'monospace'

type ActiveFormats = Record<RichTextFormat, boolean>

type EditorHistoryEntry = {
  text: string
}

const emptyActiveFormats = (): ActiveFormats => ({
  bold: false,
  italic: false,
  strikethrough: false,
  monospace: false,
})

const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const markdownToHtml = (text: string) => {
  const formatPattern =
    /```(?=\S)([\s\S]*?\S)```|\*(?=\S)([^*\n]*?\S)\*|_(?=\S)([^_\n]*?\S)_|~(?=\S)([^~\n]*?\S)~/g
  let html = ''
  let lastIndex = 0

  for (const match of text.matchAll(formatPattern)) {
    html += escapeHtml(text.slice(lastIndex, match.index)).replace(/\n/g, '<br>')

    const [raw, code, bold, italic, strike] = match
    const escapedRaw = escapeHtml(raw)

    if (code !== undefined) {
      html += `<code>${escapedRaw}</code>`
    } else if (bold !== undefined) {
      html += `<strong>${escapedRaw}</strong>`
    } else if (italic !== undefined) {
      html += `<em>${escapedRaw}</em>`
    } else if (strike !== undefined) {
      html += `<del>${escapedRaw}</del>`
    }

    lastIndex = (match.index ?? 0) + raw.length
  }

  html += escapeHtml(text.slice(lastIndex)).replace(/\n/g, '<br>')
  if (text.endsWith('\n')) html += '<br>'
  return html
}

const textForNode = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? ''
  if (!(node instanceof HTMLElement)) return ''

  if (node.tagName === 'BR') return '\n'

  const content = Array.from(node.childNodes).map(textForNode).join('')
  if (node.tagName === 'DIV' || node.tagName === 'P') return `${content}\n`

  return content
}

const htmlToMarkdown = (element: HTMLElement) =>
  Array.from(element.childNodes).map(textForNode).join('').replace(/\n$/, '')

const textLengthForNode = (node: Node): number => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent?.length ?? 0
  if (node instanceof HTMLBRElement) return 1
  if (!(node instanceof HTMLElement)) return 0

  const contentLength = Array.from(node.childNodes).reduce(
    (length, child) => length + textLengthForNode(child),
    0,
  )

  return node.tagName === 'DIV' || node.tagName === 'P' ? contentLength + 1 : contentLength
}

const getTextOffsetForPosition = (root: HTMLElement, container: Node, offset: number) => {
  let textOffset = 0
  let found = false

  const visit = (node: Node) => {
    if (found) return

    if (node === container) {
      if (node.nodeType === Node.TEXT_NODE) {
        textOffset += offset
      } else {
        const childNodes = Array.from(node.childNodes).slice(0, offset)
        textOffset += childNodes.reduce((length, child) => length + textLengthForNode(child), 0)
      }

      found = true
      return
    }

    if (node.nodeType === Node.TEXT_NODE || node instanceof HTMLBRElement) {
      textOffset += textLengthForNode(node)
      return
    }

    Array.from(node.childNodes).forEach(visit)

    if (!found && node instanceof HTMLElement && (node.tagName === 'DIV' || node.tagName === 'P')) {
      textOffset += 1
    }
  }

  visit(root)
  return textOffset
}

const placeCaretAtEnd = (element: HTMLElement) => {
  const range = document.createRange()
  range.selectNodeContents(element)
  range.collapse(false)

  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
}

const setCaretOffset = (element: HTMLElement, offset: number) => {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
  let remainingOffset = offset
  let currentNode = walker.nextNode()

  while (currentNode) {
    const textLength = currentNode.textContent?.length ?? 0
    if (remainingOffset <= textLength) {
      const range = document.createRange()
      range.setStart(currentNode, remainingOffset)
      range.collapse(true)

      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(range)
      return
    }

    remainingOffset -= textLength
    currentNode = walker.nextNode()
  }

  placeCaretAtEnd(element)
}

const setSelectionOffsets = (element: HTMLElement, start: number, end: number) => {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []
  let currentNode = walker.nextNode() as Text | null

  while (currentNode) {
    textNodes.push(currentNode)
    currentNode = walker.nextNode() as Text | null
  }

  let accumulated = 0
  let startNode = textNodes[textNodes.length - 1]
  let endNode = startNode
  let startOffset = startNode?.textContent?.length ?? 0
  let endOffset = startOffset

  for (const node of textNodes) {
    const length = node.textContent?.length ?? 0
    if (start >= accumulated && start <= accumulated + length) {
      startNode = node
      startOffset = start - accumulated
    }
    if (end >= accumulated && end <= accumulated + length) {
      endNode = node
      endOffset = end - accumulated
      break
    }
    accumulated += length
  }

  if (!startNode || !endNode) {
    placeCaretAtEnd(element)
    return
  }

  const range = document.createRange()
  range.setStart(startNode, startOffset)
  range.setEnd(endNode, endOffset)
  const selection = window.getSelection()
  selection?.removeAllRanges()
  selection?.addRange(range)
}

const markerByFormat: Record<RichTextFormat, string> = {
  bold: '*',
  italic: '_',
  strikethrough: '~',
  monospace: '```',
}

const trimTrailingSelectionWhitespace = (text: string, start: number, end: number) => {
  let trimmedEnd = end

  while (trimmedEnd > start && /[ \t]/.test(text[trimmedEnd - 1] ?? '')) {
    trimmedEnd -= 1
  }

  return trimmedEnd
}

const getActiveFormatsAtOffset = (text: string, offset: number): ActiveFormats => {
  const activeFormats = emptyActiveFormats()

  for (const [format, marker] of Object.entries(markerByFormat) as [RichTextFormat, string][]) {
    const hasOpeningMarker = text.slice(offset - marker.length, offset) === marker
    const hasClosingMarker = text.slice(offset, offset + marker.length) === marker
    if (hasOpeningMarker && hasClosingMarker) {
      activeFormats[format] = true
    }
  }

  const formatPattern =
    /```(?=\S)([\s\S]*?\S)```|\*(?=\S)([^*\n]*?\S)\*|_(?=\S)([^_\n]*?\S)_|~(?=\S)([^~\n]*?\S)~/g

  for (const match of text.matchAll(formatPattern)) {
    const [raw, code, bold, italic, strike] = match
    const start = match.index ?? 0
    const end = start + raw.length

    let format: RichTextFormat
    if (code !== undefined) format = 'monospace'
    else if (bold !== undefined) format = 'bold'
    else if (italic !== undefined) format = 'italic'
    else if (strike !== undefined) format = 'strikethrough'
    else continue

    const markerLength = markerByFormat[format].length
    const contentStart = start + markerLength
    const contentEnd = end - markerLength

    if (offset >= contentStart && offset <= contentEnd) {
      activeFormats[format] = true
    }
  }

  return activeFormats
}

const getFormatRangeAtOffset = (
  text: string,
  offset: number,
  format: RichTextFormat,
): { start: number; end: number } | null => {
  const formatPattern =
    /```(?=\S)([\s\S]*?\S)```|\*(?=\S)([^*\n]*?\S)\*|_(?=\S)([^_\n]*?\S)_|~(?=\S)([^~\n]*?\S)~/g
  const marker = markerByFormat[format]

  for (const match of text.matchAll(formatPattern)) {
    const [raw, code, bold, italic, strike] = match
    const matchedFormat =
      code !== undefined
        ? 'monospace'
        : bold !== undefined
          ? 'bold'
          : italic !== undefined
            ? 'italic'
            : strike !== undefined
              ? 'strikethrough'
              : null

    if (matchedFormat !== format) continue

    const matchStart = match.index ?? 0
    const contentStart = matchStart + marker.length
    const contentEnd = matchStart + raw.length - marker.length

    if (offset >= contentStart && offset <= contentEnd) {
      return { start: contentStart, end: contentEnd }
    }
  }

  return null
}

export const useRichWhatsappEditor = (messageText: Ref<string>) => {
  const editorRef = ref<HTMLElement>()
  const activeFormats = ref<ActiveFormats>(emptyActiveFormats())
  const undoHistory: EditorHistoryEntry[] = []
  const redoHistory: EditorHistoryEntry[] = []

  const saveUndoState = () => {
    const lastEntry = undoHistory[undoHistory.length - 1]
    if (lastEntry?.text === messageText.value) return

    undoHistory.push({ text: messageText.value })
    redoHistory.length = 0
  }

  const syncEditorFromText = (force = false) => {
    const editor = editorRef.value
    if (!editor || (!force && htmlToMarkdown(editor) === messageText.value)) return

    const isFocused = document.activeElement === editor
    editor.innerHTML = markdownToHtml(messageText.value)

    if (isFocused) {
      placeCaretAtEnd(editor)
    }
  }

  const syncTextFromEditor = () => {
    const editor = editorRef.value
    if (!editor) return

    const selectionOffsets = getSelectionOffsets()
    const nextText = htmlToMarkdown(editor)
    if (nextText !== messageText.value) {
      saveUndoState()
      messageText.value = nextText
    }

    // Re-render only when the generated HTML differs. Replacing the DOM on
    // every input breaks the browser selection, especially after a multiline
    // paste. When a render is needed, restore both caret endpoints.
    const formattedHtml = markdownToHtml(messageText.value)
    if (editor.innerHTML !== formattedHtml) {
      editor.innerHTML = formattedHtml

      if (selectionOffsets) {
        setSelectionOffsets(editor, selectionOffsets.start, selectionOffsets.end)
      } else {
        placeCaretAtEnd(editor)
      }
    }

    updateActiveFormats()
  }

  const focusEditor = () => {
    nextTick(() => editorRef.value?.focus())
  }

  const toggleFormat = (format: RichTextFormat) => {
    const editor = editorRef.value
    if (!editor) return

    editor.focus()
    const marker = markerByFormat[format]
    const markerLength = marker.length
    const offsets = getSelectionOffsets()
    const start = offsets?.start ?? messageText.value.length
    const end = trimTrailingSelectionWhitespace(
      messageText.value,
      start,
      offsets?.end ?? start,
    )
    const hasSelection = start !== end
    const isWrapped =
      messageText.value.slice(start - markerLength, start) === marker &&
      messageText.value.slice(end, end + markerLength) === marker

    let nextText = messageText.value
    let nextStart = start
    let nextEnd = end

    saveUndoState()

    if (isWrapped) {
      // The format is already active for this selection: remove only its
      // surrounding markers instead of adding another pair.
      nextText =
        messageText.value.slice(0, start - markerLength) +
        messageText.value.slice(start, end) +
        messageText.value.slice(end + markerLength)
      nextStart = start - markerLength
      nextEnd = hasSelection ? end - markerLength : nextStart
    } else if (hasSelection) {
      nextText =
        messageText.value.slice(0, start) +
        marker +
        messageText.value.slice(start, end) +
        marker +
        messageText.value.slice(end)
      nextStart = start + markerLength
      nextEnd = end + markerLength
    } else {
      const formattedRange = getFormatRangeAtOffset(messageText.value, start, format)

      if (formattedRange) {
        // The caret is inside an already formatted span: toggle off the whole
        // span, even when it is not immediately next to either marker.
        nextText =
          messageText.value.slice(0, formattedRange.start - markerLength) +
          messageText.value.slice(formattedRange.start, formattedRange.end) +
          messageText.value.slice(formattedRange.end + markerLength)
        nextStart = start - markerLength
        nextEnd = nextStart
      } else {
        // With no selection, create one empty formatted span and place the
        // caret inside it for the following typed characters.
        nextText =
          messageText.value.slice(0, start) + marker + marker + messageText.value.slice(end)
        nextStart = start + markerLength
        nextEnd = nextStart
      }
    }

    messageText.value = nextText

    nextTick(() => {
      syncEditorFromText(true)
      if (hasSelection && nextStart !== nextEnd) {
        setSelectionOffsets(editor, nextStart, nextEnd)
      } else {
        setCaretOffset(editor, nextEnd)
      }

      activeFormats.value = getActiveFormatsAtOffset(messageText.value, nextEnd)
    })
  }

  const updateActiveFormats = () => {
    const offsets = getSelectionOffsets()
    activeFormats.value = getActiveFormatsAtOffset(messageText.value, offsets?.start ?? 0)
  }

  const getSelectionOffsets = () => {
    const editor = editorRef.value
    const selection = window.getSelection()
    if (!editor || !selection?.rangeCount) return null

    const range = selection.getRangeAt(0)
    if (!editor.contains(range.commonAncestorContainer)) return null

    return {
      start: getTextOffsetForPosition(editor, range.startContainer, range.startOffset),
      end: getTextOffsetForPosition(editor, range.endContainer, range.endOffset),
    }
  }

  const insertLineBreak = () => {
    const editor = editorRef.value
    const selection = window.getSelection()
    if (!editor || !selection?.rangeCount) return

    editor.focus()
    const range = selection.getRangeAt(0)
    if (!editor.contains(range.commonAncestorContainer)) return

    range.deleteContents()

    const lineBreak = document.createElement('br')
    range.insertNode(lineBreak)
    range.setStartAfter(lineBreak)
    range.collapse(true)

    selection.removeAllRanges()
    selection.addRange(range)
    syncTextFromEditor()
  }

  const insertText = (text: string) => {
    const editor = editorRef.value
    if (!editor) return

    editor.focus()
    const offsets = getSelectionOffsets()
    const start = offsets?.start ?? messageText.value.length
    const end = offsets?.end ?? start

    saveUndoState()
    messageText.value = messageText.value.slice(0, start) + text + messageText.value.slice(end)

    nextTick(() => {
      syncEditorFromText(true)
      const caretOffset = start + text.length
      setCaretOffset(editor, caretOffset)
      activeFormats.value = getActiveFormatsAtOffset(messageText.value, caretOffset)
    })
  }

  const handlePaste = (event: ClipboardEvent) => {
    const text = event.clipboardData?.getData('text/plain')
    if (text === undefined) return

    event.preventDefault()
    insertText(text)
  }

  const handleUndo = (event: KeyboardEvent) => {
    if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z') return false

    const previousState = undoHistory.pop()
    if (!previousState) return false

    event.preventDefault()
    redoHistory.push({ text: messageText.value })
    messageText.value = previousState.text

    nextTick(() => {
      syncEditorFromText(true)
      placeCaretAtEnd(editorRef.value as HTMLElement)
      activeFormats.value = getActiveFormatsAtOffset(
        messageText.value,
        messageText.value.length,
      )
    })

    return true
  }

  const handleRedo = (event: KeyboardEvent) => {
    if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'y') return false

    const nextState = redoHistory.pop()
    if (!nextState) return false

    event.preventDefault()
    undoHistory.push({ text: messageText.value })
    messageText.value = nextState.text

    nextTick(() => {
      syncEditorFromText(true)
      placeCaretAtEnd(editorRef.value as HTMLElement)
      activeFormats.value = getActiveFormatsAtOffset(
        messageText.value,
        messageText.value.length,
      )
    })

    return true
  }

  const handleFormatKeydown = (event: KeyboardEvent) => {
    if (!(event.ctrlKey || event.metaKey)) return false

    const key = event.key.toLowerCase()
    const formatByKey: Partial<Record<string, RichTextFormat>> = {
      b: 'bold',
      i: 'italic',
      s: 'strikethrough',
      m: 'monospace',
    }
    const format = formatByKey[key]
    if (!format) return false

    event.preventDefault()
    toggleFormat(format)
    return true
  }

  return {
    editorRef,
    activeFormats,
    syncEditorFromText,
    syncTextFromEditor,
    focusEditor,
    toggleFormat,
    updateActiveFormats,
    insertText,
    insertLineBreak,
    handlePaste,
    handleFormatKeydown,
    handleUndo,
    handleRedo,
    recordUndoState: saveUndoState,
  }
}
